import { GetOrganisationHierarchyQuery } from '../gql/graphql.generated';

export type OrganisationDivision = NonNullable<GetOrganisationHierarchyQuery['organisationHierarchy']>;
export type OrganisationLocation = NonNullable<NonNullable<OrganisationDivision['locations']>[number]>;

export interface DivisionNode {
  id: string;
  divisionId: string;
  name: string;
  type: 'division';
  children: DivisionNode[];
  locations: LocationNode[];
  parentId?: string;
}

export interface LocationNode {
  id: string;
  name: string;
  type: 'location';
  locationId: number;
  parentId: string;
}

export type TreeNode = DivisionNode | LocationNode;

export const getChildDivisions = (division: OrganisationDivision) => {
  return (division.children ?? []).filter((child): child is OrganisationDivision => !!child);
};

export const getLocations = (division: OrganisationDivision) => {
  return (division.locations ?? []).filter((location): location is OrganisationLocation => !!location);
};

export const countDivisions = (division: OrganisationDivision): number => {
  return 1 + getChildDivisions(division).reduce((total, child) => total + countDivisions(child), 0);
};

export const countLocations = (division: OrganisationDivision): number => {
  return (
    getLocations(division).length +
    getChildDivisions(division).reduce((total, child) => total + countLocations(child), 0)
  );
};

export const buildDivisionNode = (division: OrganisationDivision, parentId?: string): DivisionNode => {
  const id = `division-${division.divisionId ?? division.name}-${division.level}`;

  return {
    id,
    divisionId: division.divisionId ?? division.name,
    name: division.name,
    type: 'division',
    parentId,
    children: getChildDivisions(division).map(child => buildDivisionNode(child, id)),
    locations: getLocations(division).map(location => ({
      id: `location-${location.id}`,
      name: location.name,
      type: 'location',
      locationId: location.id,
      parentId: id
    }))
  };
};

export const collectInitialExpandedIds = (root: DivisionNode) => {
  return new Set<string>([root.id, ...root.children.map(child => child.id)]);
};

export const flattenTree = (node: DivisionNode, map: Map<string, TreeNode>) => {
  map.set(node.id, node);
  node.locations.forEach(location => {
    map.set(location.id, location);
  });
  node.children.forEach(child => flattenTree(child, map));
  return map;
};

export const buildPath = (nodeId: string, nodeMap: Map<string, TreeNode>) => {
  const path: TreeNode[] = [];
  let current = nodeMap.get(nodeId);

  while (current) {
    path.unshift(current);
    const parentId = current.parentId;
    current = parentId ? nodeMap.get(parentId) : undefined;
  }

  return path;
};

export const expandToNode = (nodeId: string, nodeMap: Map<string, TreeNode>) => {
  const path = buildPath(nodeId, nodeMap);
  return new Set<string>(path.filter((node): node is DivisionNode => node.type === 'division').map(node => node.id));
};
